/**
 * auto-captions-align — align a user's OWN text to the audio timeline.
 *
 * Automatic speech recognition cannot read singing (it returns garbled
 * lyrics). But it IS good at finding WHEN there is vocal activity — the
 * word-level timestamps Whisper produces are a usable "timing skeleton"
 * even when the words themselves are wrong. So when a user supplies the
 * correct text (e.g. the real song lyrics), we keep Whisper's timing
 * anchors and lay the user's words onto them. The result is the user's
 * correct words appearing roughly when the vocals actually happen — far
 * better than spreading the text evenly (which ignores instrumental gaps).
 *
 * `alignToAnchors(text, anchors, totalDur)`:
 *   - tokenizes `text` into words (whitespace-split, newlines collapsed);
 *   - maps token i onto the anchor INDEX space (not raw time) so words
 *     follow vocal DENSITY — during an instrumental gap there are few
 *     anchors, so few words land there, instead of smearing across silence;
 *   - returns a flat [{text,start,end}] array. The caller is expected to run
 *     it through the timing sanitizer (auto-captions-timings.js) to enforce
 *     minimum durations / monotonicity. With no usable anchors it falls back
 *     to an even spread across [0, totalDur].
 *
 * Exposed as `window.__acAlign` (browser) and module.exports (node tests).
 */
(function () {
    'use strict';

    function tokenize(text) {
        return String(text == null ? '' : text)
            .replace(/\s+/g, ' ')
            .trim()
            .split(' ')
            .filter(Boolean);
    }

    function num(v, fallback) { return (typeof v === 'number' && isFinite(v)) ? v : fallback; }

    // Even fallback when there are no timing anchors at all.
    function evenSpread(tokens, totalDur) {
        var dur = (typeof totalDur === 'number' && isFinite(totalDur) && totalDur > 0) ? totalDur : Math.max(1, tokens.length * 0.5);
        var step = dur / tokens.length;
        var out = [];
        for (var i = 0; i < tokens.length; i++) {
            out.push({ text: tokens[i], start: i * step, end: (i + 1) * step });
        }
        return out;
    }

    /* ── Content alignment: use WHAT the ASR heard, not just when ─────────────
       The index mapping below (alignByIndex) puts token i at anchor index
       i*(N-1)/(M-1). That is only correct when the recogniser returned exactly
       as many words as the user pasted, and it never does: it misses quiet
       words and hears ad-libs. Measured on real whisper.cpp output against a
       clean-audio ground truth (18 FLEURS clips joined into a 196 s track,
       324 reference words, music bed mixed in at 8 levels, two models):

         anchors from      corroborated   median error: index -> content
         clean audio            75 %          3.51 s  ->  0.00 s
         bed 0.15               54 %         14.25 s  ->  2.48 s
         bed 0.25               54 %          7.47 s  ->  1.16 s
         bed 0.35               40 %         11.55 s  ->  3.87 s
         bed 0.50               39 %         10.88 s  ->  2.77 s
         bed 0.70               15 %         74.26 s  -> 15.46 s
         bed 0.70 (turbo)       41 %          8.00 s  ->  3.67 s

       Note the clean row: even with PERFECT anchors the index mapping put the
       median word 3.5 s from where it was spoken, and only 15 % of words within
       a second — because whisper's word count never equals the user's.

       ⚠️ It is NOT always better. At bed 1.2 whisper returned "[музыка]" 45
       times: nothing to match, and confident nonsense scored 51 s against the
       index mapping's 21 s. So this is GATED on how much of the user's text the
       ASR actually corroborates. The gate has a wide safe plateau — every
       threshold from 3 % to 12 % picked correctly on all 11 measured sets — so
       8 % is not a knife-edge fit.

       ⚠️ Anchors WITHOUT text corroborate nothing, so the gate falls back to the
       index mapping on its own. That is what keeps /auto-captions unchanged:
       its VAD anchors are synthetic 0.5 s slices with no text at all. */

    function normWord(s) {
        return String(s == null ? '' : s).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
    }

    /* Bigram Dice. Chosen over edit distance because it is cheap, symmetric and
       degrades smoothly: a word the recogniser half-heard still scores. */
    /* A word's bigram profile, built ONCE. The DP visits millions of cells and
       the naive form rebuilt both maps in every one of them: an hour of video
       took 14 s of blocking main thread, which would freeze /auto-captions. */
    function profileOf(str) {
        var w = normWord(str);
        var p = { w: w, n: w.length, bg: null, size: 0 };
        if (w.length >= 2) {
            p.bg = new Map();
            for (var i = 0; i + 1 < w.length; i++) {
                var g = w.slice(i, i + 2);
                p.bg.set(g, (p.bg.get(g) || 0) + 1);
            }
            p.size = w.length - 1;
        }
        return p;
    }
    function similarityOf(A, B) {
        if (!A.n || !B.n) return 0;
        if (A.w === B.w) return 1;
        if (!A.bg || !B.bg) return A.w[0] === B.w[0] ? 0.4 : 0;
        // Iterate the SMALLER profile; the counts are tiny so this is a few ops.
        var small = A.size <= B.size ? A : B, large = small === A ? B : A;
        var inter = 0;
        small.bg.forEach(function (n, g) {
            var m = large.bg.get(g);
            if (m > 0) inter += (n < m ? n : m);
        });
        var tot = A.size + B.size;
        return tot ? (2 * inter / tot) : 0;
    }
    function similarity(a, b) { return similarityOf(profileOf(a), profileOf(b)); }

    /* Needleman-Wunsch, BANDED. The full matrix is O(M*N) and this also serves
       /auto-captions, where an hour of video can mean ~10 000 words against
       ~12 000 anchors — 120 M cells, which would be ~1 GB and is not shippable.
       Both sequences describe the SAME audio in the same order, so the true
       path never strays far from the diagonal; a band around it costs
       O(M*band) and changes nothing about the result in practice.
       Returns matchedAnchorIndex[] with null where a word matched nothing. */
    function alignSequences(words, anchors) {
        var M = words.length, N = anchors.length;
        if (!M || !N) return new Array(M).fill(null);
        var GAP = -0.45, BIAS = 0.30;
        /* The band is capped as well as proportional. Both sequences describe the
           same audio in order, so the true path hugs the diagonal; a wider band
           on a long file buys nothing and costs quadratically. */
        var band = Math.max(48, Math.min(400, Math.ceil(0.08 * Math.max(M, N))));
        var width = 2 * band + 1;
        var ratio = N / M;
        // centre of the band on row i, in anchor-index space
        function centre(i) { return Math.round(i * ratio); }

        var wordP = new Array(M), anchP = new Array(N), z;
        for (z = 0; z < M; z++) wordP[z] = profileOf(words[z]);
        for (z = 0; z < N; z++) anchP[z] = profileOf(anchors[z] && anchors[z].text);

        var NEG = -1e18;
        var prev = new Float64Array(width), cur = new Float64Array(width);
        // 0 = diagonal (match), 1 = word consumed alone, 2 = anchor consumed alone
        var ptr = new Uint8Array(M * width);
        var c0 = centre(0), j, k;
        for (k = 0; k < width; k++) {
            j = c0 - band + k;
            prev[k] = (j < 0) ? NEG : (j === 0 ? 0 : j * GAP);
        }
        for (var i = 1; i <= M; i++) {
            var ci = centre(i), cp = centre(i - 1), wp = wordP[i - 1];
            for (k = 0; k < width; k++) {
                j = ci - band + k;
                if (j < 0 || j > N) { cur[k] = NEG; continue; }
                var best = NEG, p = 1;
                // diagonal: (i-1, j-1)
                var kd = (j - 1) - (cp - band);
                if (j >= 1 && kd >= 0 && kd < width && prev[kd] > NEG / 2) {
                    best = prev[kd] + (similarityOf(wp, anchP[j - 1]) - BIAS);
                    p = 0;
                }
                // up: (i-1, j) — the word has no anchor
                var ku = j - (cp - band);
                if (ku >= 0 && ku < width && prev[ku] > NEG / 2) {
                    var up = prev[ku] + GAP;
                    if (up > best) { best = up; p = 1; }
                }
                // left: (i, j-1) — the anchor has no word
                if (k >= 1 && cur[k - 1] > NEG / 2) {
                    var left = cur[k - 1] + GAP;
                    if (left > best) { best = left; p = 2; }
                }
                cur[k] = best;
                ptr[(i - 1) * width + k] = p;
            }
            var t = prev; prev = cur; cur = t;
        }
        // Trace back from (M, N)
        var out = new Array(M).fill(null);
        var ii = M, jj = N;
        while (ii > 0 && jj > 0) {
            var ck = centre(ii) - band;
            var kk = jj - ck;
            if (kk < 0 || kk >= width) break;          // fell out of the band
            var pp = ptr[(ii - 1) * width + kk];
            if (pp === 0) { out[ii - 1] = jj - 1; ii--; jj--; }
            else if (pp === 1) { ii--; }
            else { jj--; }
        }
        return out;
    }

    /* Share of the user's words the ASR independently corroborates. Computable
       without any ground truth, and it is exactly what collapses when the
       recogniser heard music rather than words. */
    function corroborationRate(words, anchors, matches) {
        if (!words.length) return 0;
        var strong = 0;
        for (var k = 0; k < words.length; k++) {
            var j = matches[k];
            if (j != null && similarityOf(profileOf(words[k]), profileOf(anchors[j].text)) >= 0.6) strong++;
        }
        return strong / words.length;
    }

    /* Matched words take their anchor's time; the rest are interpolated between
       their matched neighbours, so a word the ASR never heard still lands
       between the two it did. */
    function timesFromMatches(words, anchors, matches, lastEnd) {
        var t = new Array(words.length).fill(null), k;
        for (k = 0; k < words.length; k++) if (matches[k] != null) t[k] = anchors[matches[k]].start;
        var prev = -1, first = -1;
        for (k = 0; k < t.length; k++) {
            if (t[k] == null) continue;
            if (first < 0) first = k;
            if (prev >= 0 && prev < k - 1) {
                for (var m = prev + 1; m < k; m++) {
                    t[m] = t[prev] + (t[k] - t[prev]) * (m - prev) / (k - prev);
                }
            }
            prev = k;
        }
        if (prev < 0) return null;                       // nothing matched at all
        // Head: walk back from the first matched word; Tail: forward from the last.
        var STEP = 0.4;
        for (k = first - 1; k >= 0; k--) t[k] = Math.max(0, t[k + 1] - STEP);
        var tailEnd = (typeof lastEnd === 'number' && lastEnd > t[prev]) ? lastEnd : t[prev] + (t.length - prev) * STEP;
        for (k = prev + 1; k < t.length; k++) {
            t[k] = t[prev] + (tailEnd - t[prev]) * (k - prev) / (t.length - prev);
        }
        return t;
    }

    function alignToAnchors(text, anchors, totalDur) {
        var tokens = tokenize(text);
        if (!tokens.length) return [];

        // Keep only anchors with a finite start, sorted ascending. These are
        // the "when did sound happen" skeleton; their text is irrelevant.
        var starts = [];
        if (anchors && anchors.length) {
            for (var a = 0; a < anchors.length; a++) {
                var s = num(anchors[a] && anchors[a].start, NaN);
                // `text` rides along: content alignment needs WHAT was heard,
                // and this sort is the only place anchor order is established.
                if (isFinite(s)) starts.push({ s: s, e: num(anchors[a].end, s), text: anchors[a].text });
            }
            starts.sort(function (x, y) { return x.s - y.s; });
            // alignSequences/timesFromMatches speak .start; keep one shape.
            for (var b = 0; b < starts.length; b++) starts[b].start = starts[b].s;
        }
        if (!starts.length) return evenSpread(tokens, totalDur);

        var N = starts.length, M = tokens.length;
        var lastEnd = Math.max(starts[N - 1].e, starts[N - 1].s);
        var out = new Array(M);

        /* Content alignment first — see the note above. `starts` is rebuilt in
           time order, so the text has to be carried along with it or the words
           would be compared against the wrong anchors. */
        if (CONTENT_GATE > 0) {
            var matches = alignSequences(tokens, starts);
            var rate = corroborationRate(tokens, starts, matches);
            if (rate >= CONTENT_GATE) {
                var times = timesFromMatches(tokens, starts, matches, lastEnd);
                if (times) {
                    for (var q = 0; q < M; q++) {
                        out[q] = { text: tokens[q], start: times[q], end: times[q] };
                    }
                    for (var r = 0; r < M; r++) {
                        out[r].end = (r + 1 < M) ? out[r + 1].start : Math.max(lastEnd, out[r].start + 0.3);
                        if (out[r].end <= out[r].start) out[r].end = out[r].start + 0.3;
                    }
                    return out;
                }
            }
        }

        for (var i = 0; i < M; i++) {
            // Fractional position of token i in anchor-index space.
            var pos = (M > 1) ? (i * (N - 1) / (M - 1)) : 0;
            var lo = Math.floor(pos);
            var hi = Math.min(lo + 1, N - 1);
            var frac = pos - lo;
            var t = starts[lo].s + (starts[hi].s - starts[lo].s) * frac;
            out[i] = { text: tokens[i], start: t, end: t };
        }
        // Contiguous ends (next start); last word runs to the last anchor end.
        for (var k = 0; k < M; k++) {
            out[k].end = (k + 1 < M) ? out[k + 1].start : Math.max(lastEnd, out[k].start + 0.3);
            if (out[k].end <= out[k].start) out[k].end = out[k].start + 0.3;
        }
        return out;
    }

    /* 8 % corroborated. Every threshold from 3 % to 12 % picked correctly on all
       11 measured ASR sets, so this sits mid-plateau rather than on an edge. */
    var CONTENT_GATE = 0.08;

    var api = {
        alignToAnchors: alignToAnchors, tokenize: tokenize,
        // Exposed for the measurement harness and the tests, not for tools.
        _similarity: similarity, _alignSequences: alignSequences,
        _corroborationRate: corroborationRate,
        _setContentGate: function (v) { CONTENT_GATE = (typeof v === 'number') ? v : 0.08; },
    };
    if (typeof window !== 'undefined') window.__acAlign = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
